import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ColorField
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    DocsTable,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "ColorField";

const USAGE_CODE = `const [color, setColor] = useState("#006155");

<ColorField
    hintText="Shown on cards and in the calendar."
    onChange={setColor}
    title="Label colour"
    value={color}
/>`;

const TYPING_CODE = `<ColorField
    title="Brand colour"
    initialValue="#006155"
    onChange={(value) => preview(value)}
    onChangeEnd={(value) => save(value)}
/>`;

const PICKER_CODE = `<ColorField
    recentColors={["#be3600", "#826200"]}
    contrastSurface="#ffffff"
    isEyeDropperEnabled={true}
    title="Series colour"
/>

<ColorField
    isPickerEnabled={false}
    title="Series colour"
/>`;

const ALPHA_CODE = `<ColorField
    initialValue="#00615580"
    isAlphaEnabled={true}
    title="Shadow colour"
/>`;

const STATES_CODE = `<ColorField
    size="small"
    title="Label"
/>

<ColorField
    isReadOnly={true}
    title="Label"
/>`;

const DEFAULT_COLOR = "#006155";

const ALPHA_COLOR = "#00615580";

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("colorFieldPage.typingTitle"),
        id: "typing"
    },
    {
        title: t("colorFieldPage.pickerTitle"),
        id: "picker"
    },
    {
        title: t("colorFieldPage.recentTitle"),
        id: "recent"
    },
    {
        title: t("colorFieldPage.alphaTitle"),
        id: "alpha"
    },
    {
        title: t("colorFieldPage.statesTitle"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const OverviewPreview = () => {
    const {
        t
    } = useDocsI18n();

    const [
        color,
        setColor
    ] = useState<string>(DEFAULT_COLOR);

    return <Preview
        caption={t("colorFieldPage.overviewCaption")}
        isStacked={true}
    >
        <ColorField
            hintText={t("colorFieldPage.overviewHint")}
            title={t("colorFieldPage.overviewTitle")}
            spreadBehaviour="stretch"
            onChange={setColor}
            value={color}
        />
    </Preview>;
};

const ColorFieldPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("colorFieldPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorFieldPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("colorFieldPage.overviewNote")}
            </Paragraph>
            <OverviewPreview />
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("colorFieldPage.typingTitle")}
            id="typing"
        >
            <Paragraph>
                {t("colorFieldPage.typingText")}
            </Paragraph>
            <Paragraph>
                {t("colorFieldPage.typingInvalid")}
            </Paragraph>
            <Preview
                caption={t("colorFieldPage.typingCaption")}
                isStacked={true}
            >
                <ColorField
                    title={t("colorFieldPage.overviewTitle")}
                    initialValue={DEFAULT_COLOR}
                    spreadBehaviour="stretch"
                />
            </Preview>
            <CodeBlock
                code={TYPING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorFieldPage.pickerTitle")}
            id="picker"
        >
            <Paragraph>
                {t("colorFieldPage.pickerText")}
            </Paragraph>
            <Paragraph>
                {t("colorFieldPage.pickerSheet")}
            </Paragraph>
            <Paragraph>
                {t("colorFieldPage.pickerPassThrough")}
            </Paragraph>
            <Preview
                caption={t("colorFieldPage.pickerCaption")}
                isStacked={true}
            >
                <ColorField
                    title={t("colorFieldPage.overviewTitle")}
                    initialValue={DEFAULT_COLOR}
                    spreadBehaviour="stretch"
                />
            </Preview>
            <CodeBlock
                code={PICKER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorFieldPage.recentTitle")}
            id="recent"
        >
            <Paragraph>
                {t("colorFieldPage.recentText")}
            </Paragraph>
        </Section>
        <Section
            title={t("colorFieldPage.alphaTitle")}
            id="alpha"
        >
            <Paragraph>
                {t("colorFieldPage.alphaText")}
            </Paragraph>
            <Preview
                caption={t("colorFieldPage.alphaCaption")}
                isStacked={true}
            >
                <ColorField
                    title={t("colorFieldPage.overviewTitle")}
                    initialValue={ALPHA_COLOR}
                    spreadBehaviour="stretch"
                    isAlphaEnabled={true}
                />
            </Preview>
            <CodeBlock
                code={ALPHA_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorFieldPage.statesTitle")}
            id="states"
        >
            <Paragraph>
                {t("colorFieldPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("colorFieldPage.statesCaption")}
                isStacked={true}
            >
                {
                    SIZES.map((size) => <ColorField
                        initialValue={DEFAULT_COLOR}
                        spreadBehaviour="stretch"
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
                <ColorField
                    initialValue={DEFAULT_COLOR}
                    spreadBehaviour="stretch"
                    title="isReadOnly"
                    isReadOnly={true}
                />
                <ColorField
                    initialValue={DEFAULT_COLOR}
                    spreadBehaviour="stretch"
                    title="isDisabled"
                    isDisabled={true}
                />
                <ColorField
                    initialValue={DEFAULT_COLOR}
                    spreadBehaviour="stretch"
                    title="isLoading"
                    isLoading={true}
                />
            </Preview>
            <CodeBlock
                code={STATES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("colorFieldPage.a11yTitle"),
                    t("colorFieldPage.a11yOpen"),
                    t("colorFieldPage.a11yError"),
                    t("colorFieldPage.a11yFocus"),
                    t("colorFieldPage.a11yTouch")
                ]}
            />
            <DocsTable
                rows={[
                    [
                        <InlineCode
                            key="tab"
                        >
                            {t("colorFieldPage.keyTab")}
                        </InlineCode>,
                        t("colorFieldPage.keyTabAction")
                    ],
                    [
                        <InlineCode
                            key="enter"
                        >
                            {t("colorFieldPage.keyEnter")}
                        </InlineCode>,
                        t("colorFieldPage.keyEnterAction")
                    ],
                    [
                        <InlineCode
                            key="space"
                        >
                            {t("colorFieldPage.keySpace")}
                        </InlineCode>,
                        t("colorFieldPage.keySpaceAction")
                    ],
                    [
                        <InlineCode
                            key="escape"
                        >
                            {t("colorFieldPage.keyEscape")}
                        </InlineCode>,
                        t("colorFieldPage.keyEscapeAction")
                    ]
                ]}
                headers={[
                    t("colorFieldPage.keyColumn"),
                    t("colorFieldPage.actionColumn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("colorFieldPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("colorFieldPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("colorFieldPage.propOnChange"),
                        type: "(value: string) => void",
                        name: "onChange"
                    },
                    {
                        description: t("colorFieldPage.propOnChangeEnd"),
                        type: "(value: string) => void",
                        name: "onChangeEnd"
                    },
                    {
                        description: t("colorFieldPage.propTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("colorFieldPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("colorFieldPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("colorFieldPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("colorFieldPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("colorFieldPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("colorFieldPage.propIsShowSubTitle"),
                        name: "isShowSubTitle",
                        type: "boolean"
                    },
                    {
                        description: t("colorFieldPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("colorFieldPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("colorFieldPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("colorFieldPage.propSize"),
                        name: "size"
                    },
                    {
                        description: t("colorFieldPage.propIsAlphaEnabled"),
                        name: "isAlphaEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("colorFieldPage.propIsPickerEnabled"),
                        name: "isPickerEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "presets, recentColors, formats, areaShape, contrastSurface, isContrastShown, isEyeDropperEnabled",
                        description: t("colorFieldPage.propPickerProps"),
                        name: "ColorPicker"
                    },
                    {
                        description: t("colorFieldPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("colorFieldPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("colorFieldPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("colorFieldPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ColorFieldPage;
